import type { ValidatorFn } from "./rules";
import { COMMONLY_CONTROLLED, PRIMARY_ENTITY_ID, isOwnershipExempt } from "../types/structureConstants";
import {
  required,
  fileRequired,
  ein,
  zipCode,
  sicCode,
  validSicCode,
  year,
  minValue,
  maxValue,
  email,
  usPhone,
  minArrayLength,
  employeesComplete,
  affiliatedGroupsComplete,
  ownersComplete,
  ownershipAssignmentsComplete,
  OWNER_REQUIRED_MESSAGE,
} from "./rules";
import type { IntakeFormData, SicCodeEntry } from "../types";
import { useFormStore } from "../store/formStore";
import { isRequiredDocsVisible, isOwnerEligibilityVisible } from "../hooks/step7Rules";

/**
 * Per-step validation schemas.
 *
 * Each schema maps field names to an array of validators.
 * A schema can be a static object OR a function of form state
 * (for conditional validation, e.g. fiduciary fields only required
 * when fiduciarySameAsContact is "No").
 *
 * Per-step stories will populate these.
 */
export type StepSchema =
  | Record<string, ValidatorFn[]>
  | ((state: IntakeFormData) => Record<string, ValidatorFn[]>);

// Lazily built Set of valid SIC codes — rebuilt when the cached list changes.
let _sicCodesCache: { source: SicCodeEntry[] | null; set: Set<string> } = { source: null, set: new Set() };
function getSicCodeSet(): Set<string> {
  const codes = useFormStore.getState().meta.sicCodes;
  if (codes !== _sicCodesCache.source) {
    _sicCodesCache = { source: codes, set: new Set(codes.map((c) => c.code)) };
  }
  return _sicCodesCache.set;
}

export const stepSchemas: Record<number, StepSchema> = {
  1: {
    effectiveDate: [required("Effective Date is required")],
    companyLegalName: [required("Company Legal Name is required")],
    federalTaxId: [required("Federal Tax ID is required"), ein()],
    streetAddress: [required("Street Address is required")],
    zipCode: [required("Zip Code is required"), zipCode()],
    orgType: [required("Organization Type is required")],
    sicCode: [required("SIC Code is required"), sicCode(), (v) => validSicCode(getSicCodeSet())(v)],
    yearEstablished: [required("Year Business Established is required"), year()],
    // AC: max 4 digits / 9999 per Contract.Reg_Nbr_*__c precision.
    avgW2Employees: [required("Average W2 employees is required"), minValue(1), maxValue(9999)],
    avgW2Employees20Weeks: [required("Average W2 employees (20+ weeks) is required"), minValue(1), maxValue(9999)],
  },
  2: (data: IntakeFormData) => ({
    contactName: [required("Contact Name is required")],
    fiduciarySameAsContact: [required("Please indicate if the Plan Fiduciary is the same as the Company Contact")],
    contactTitle: [required("Contact Title is required")],
    contactEmail: [required("Contact Email is required"), email()],
    contactPhone: [required("Contact Phone is required"), usPhone()],
    needAlternatePortalAccess: [required("Please indicate if an alternate contact is needed")],
    ...(data.fiduciarySameAsContact === "No"
      ? {
          fiduciaryName: [required("Fiduciary Name is required")],
          fiduciaryTitle: [required("Fiduciary Title is required")],
          fiduciaryEmail: [required("Fiduciary Email is required"), email()],
          fiduciaryPhone: [required("Fiduciary Phone is required"), usPhone()],
        }
      : {}),
    ...(data.needAlternatePortalAccess === "Yes"
      ? {
          portalContactName: [required("Portal Contact Name is required")],
          portalContactEmail: [required("Portal Contact Email is required"), email()],
          portalContactPhone: [required("Portal Contact Phone is required"), usPhone()],
          ...(data.fiduciarySameAsContact === "No"
            ? { portalReplaces: [required("Please select who this person replaces")] }
            : {}),
        }
      : {}),
  }),
  3: ((state: IntakeFormData) => {
    // Nonprofit/Other groups have no meaningful "owners" and are exempt from the ownership
    // REQUIREMENT (Mike Bentz, 2026-08-12): no minimum roster, no per-entity 100% totals.
    // Rows they do enter are still validated for completeness (ownersComplete + per-row
    // assignment checks below), so a half-filled breakdown can't slip through.
    const ownershipExempt = isOwnershipExempt(state);

    const schema: Record<string, ValidatorFn[]> = {
      // Exempt: no minimum, and the seeded empty scaffold row doesn't block (only
      // rows with actual data must be complete).
      owners: ownershipExempt
        ? [ownersComplete(true)]
        : [
            minArrayLength(1, OWNER_REQUIRED_MESSAGE),
            ownersComplete(),
          ],
      hasCba: [required("Please select Yes or No")],
      hasPeo: [required("Please select Yes or No")],
      isMultiEmployer: [required("Please select Yes or No")],
    };
    if (state.hasCba === "Yes") {
      schema.cbaUnitName = [required("CBA unit name is required")];
      schema.cbaEffectiveDate = [required("CBA effective date is required")];
    }
    if (state.hasPeo === "Yes") {
      schema.peoType = [required("Please select a PEO type")];
    }

    // Affiliated entities appear via two paths:
    //  1. isMultiEmployer=Yes + Commonly Controlled — at least one entity must be
    //     Covered Under Optimyl Policy so ownership can be captured for it.
    //  2. isMultiEmployer=No + isCommonlyControlledGroup=Yes — Covered is locked
    //     to No on this path (structure awareness only), so no covered constraint.
    let requireAffiliated = false;
    let requireAtLeastOneCovered = false;
    if (state.isMultiEmployer === "Yes") {
      schema.multiEmployerType = [required("Please select the multi-employer plan type")];
      if (state.multiEmployerType === COMMONLY_CONTROLLED) {
        requireAffiliated = true;
        requireAtLeastOneCovered = true;
      }
    } else if (state.isMultiEmployer === "No") {
      schema.isCommonlyControlledGroup = [required("Please select Yes or No")];
      if (state.isCommonlyControlledGroup === "Yes") {
        requireAffiliated = true;
      }
    }
    if (requireAffiliated) {
      schema.affiliatedGroups = [
        minArrayLength(1, "At least one affiliated entity is required"),
        affiliatedGroupsComplete(),
      ];
      if (requireAtLeastOneCovered) {
        schema.affiliatedGroups.push((value) => {
          if (!Array.isArray(value) || value.length === 0) return null;
          return value.some((g: { covered?: boolean | null }) => g.covered === true)
            ? null
            : "It is required for at least one entity other than the primary entity to be covered under the Optimyl policy.";
        });
      }
      // The primary group's "Eligible Employees" input is only visible on the
      // affiliated path (rendered inside the locked Entity 1 card) — required only
      // when the rest of the affiliated-list validation applies.
      schema.primaryEligibleEmployees = [
        required("No. of Eligible Employees is required"),
        minValue(0, "Must be 0 or greater"),
        maxValue(9999, "Maximum is 9999"),
      ];
    }

    // Ownership Breakdown: primary group always required (unless the org type is
    // ownership-exempt, above); each covered affiliated entity required only on the
    // multi-employer + Commonly Controlled path (on the locked-No path there are no
    // covered entities).
    const requiredEntityIds: string[] = ownershipExempt ? [] : [PRIMARY_ENTITY_ID];
    const entityLabelById = (id: string): string => {
      if (id === PRIMARY_ENTITY_ID) return state.companyLegalName || "Primary group";
      const idx = state.affiliatedGroups.findIndex((x) => x.id === id);
      if (idx === -1) return "Unknown entity";
      const g = state.affiliatedGroups[idx];
      return g.name || `Entity ${idx + 2}`;
    };
    if (!ownershipExempt && state.isMultiEmployer === "Yes" && state.multiEmployerType === COMMONLY_CONTROLLED) {
      for (const g of state.affiliatedGroups) {
        if (g.covered === true) requiredEntityIds.push(g.id);
      }
    }
    // Exempt org types also skip the 100%-total rule; duplicate-owner and per-row
    // completeness still apply to whatever is entered.
    schema.ownershipAssignments = [ownershipAssignmentsComplete(requiredEntityIds, entityLabelById, !ownershipExempt)];

    // Same owner can't be enrolling in benefits across multiple entities — a person
    // can only be covered by one employer's plan, so enrollingInBenefits=true is
    // exclusive per owner across the ownershipAssignments list. Only counts visible
    // (required) entities to match the UI's behavior of ignoring ghost rows.
    schema.ownershipAssignments.push((value) => {
      if (!Array.isArray(value)) return null;
      const requiredIds = new Set(requiredEntityIds);
      const enrollingCountsByOwner = new Map<string, number>();
      for (const a of value as { ownerId?: string; entityId?: string; enrollingInBenefits?: boolean | null }[]) {
        if (a.enrollingInBenefits !== true) continue;
        if (!a.ownerId || !a.entityId || !requiredIds.has(a.entityId)) continue;
        enrollingCountsByOwner.set(a.ownerId, (enrollingCountsByOwner.get(a.ownerId) ?? 0) + 1);
      }
      for (const count of enrollingCountsByOwner.values()) {
        if (count > 1) return "An owner cannot be enrolling under multiple entities.";
      }
      return null;
    });

    return schema;
  }) as StepSchema,
  4: ((state: IntakeFormData) => {
    const schema: Record<string, ValidatorFn[]> = {
      deductibleYearType: [required("Please select a deductible year type")],
      medicalContribution: [required("Medical contribution is required"), minValue(50, "Minimum employer medical contribution is 50%"), maxValue(100)],
      offeringDental: [required("Please select Yes or No")],
      offeringVision: [required("Please select Yes or No")],
      reimburseCopays: [required("Please select Yes or No")],
      offerHsa: [required("Please select Yes or No")],
      offerHra: [required("Please select Yes or No")],
      otherReimbursement: [required("Please select Yes or No")],
    };
    if (state.replacingMedical === null) schema.replacingMedical = [required("Please select Yes or No")];
    if (state.replacingMedical === "Yes") {
      schema.priorMedicalCarrier = [required("Current carrier name is required")];
      schema.priorMedicalPlanType = [required("Plan type is required")];
      schema.priorMedicalEffectiveDate = [required("Effective date is required")];
      if (state.priorMedicalCarrier === "Other") {
        schema.priorMedicalCarrierOther = [required("Please enter the carrier name")];
      }
    }
    // Prior Dental is only shown when offering Optimyl Dental, so only require its toggle then.
    if (state.offeringDental === "Yes" && state.replacingDental === null) schema.replacingDental = [required("Please select Yes or No")];
    return schema;
  }) as StepSchema,
  5: ((state: IntakeFormData) => {
    const schema: Record<string, ValidatorFn[]> = {
      excludeClass: [required("Please select Yes or No")],
      allowDomesticPartners: [required("Please select Yes or No")],
      minHoursPerWeek: [required("Minimum hours per week is required"), minValue(20), maxValue(40)],
      waitingPeriod: [required("Waiting period is required")],
      waiveWaitingPeriod: [required("Please select Yes or No")],
      wantOptimylSection125: [required("Please select Yes or No")],
    };
    if (state.excludeClass === "Yes") {
      schema.excludedClasses = [minArrayLength(1, "At least one class must be selected")];
      if (state.excludedClasses.includes("Other")) {
        schema.excludedClassesOther = [required("Please describe the excluded class")];
      }
    }
    if (state.waiveWaitingPeriod === "No") {
      schema.employeesInWaitingPeriod = [required("Please select Yes or No")];
    }
    const isSmallGroup = (state.avgW2Employees20Weeks ?? 0) < 20;
    if (isSmallGroup) {
      schema.offerCOBRAVoluntary = [required("Please select Yes or No")];
    }
    if (!isSmallGroup || state.offerCOBRAVoluntary === "Yes") {
      schema.employeesOnCOBRA = [required("Please select Yes or No")];
      schema.useOptimylCOBRA = [required("Please select Yes or No")];
    }
    if (state.wantOptimylSection125 === "Yes") {
      if (!state.section125PlanMedical && !state.section125PlanDental && !state.section125PlanVision) {
        schema.section125PlanMedical = [() => "At least one plan must be selected"];
      }
      if (!state.section125TierEmployeeOnly && !state.section125TierEmployeeSpouse && !state.section125TierEmployeeChildren && !state.section125TierEmployeeFamily) {
        schema.section125TierEmployeeOnly = [() => "At least one tier must be selected"];
      }
    }
    return schema;
  }) as StepSchema,
  6: ((state: IntakeFormData) => {
    const schema: Record<string, ValidatorFn[]> = {
      paymentMethod: [required("Payment Method is required")],
    };
    if (state.paymentMethod === "ACH") {
      schema.achAccountHolderName = [required("Account Holder Name is required")];
      schema.achBankName = [required("Bank Name is required")];
      schema.achRoutingNumber = [
        required("Routing Number is required"),
        (v) => /^\d{9}$/.test(String(v ?? "")) ? null : "Routing Number must be 9 digits",
      ];
      schema.achAccountNumber = [
        required("Account Number is required"),
        (v) => /^\d{1,17}$/.test(String(v ?? "")) ? null : "Account Number must be 1-17 digits",
      ];
      schema.achAccountType = [required("Account Type is required")];
    }
    if (state.paymentMethod === "Check") {
      // Renewals: coverage is already bound and the group pays established invoices, so no
      // binder check exists to attach (Mike Bentz, 2026-08-12) — required for new business only.
      const { isRenewal } = useFormStore.getState().meta;
      if (!isRenewal) {
        schema["documents.binderCheck"] = [fileRequired("Binder check is required")];
      }
    }
    return schema;
  }) as StepSchema,
  7: ((state: IntakeFormData) => {
    const schema: Record<string, ValidatorFn[]> = {};
    const { uwMethod, availablePlans, isRenewal } = useFormStore.getState().meta;

    // Renewals: Documents & Census are OPTIONAL (Mike Bentz, 2026-08-12) — they were collected
    // at original issue. Any employees the user chooses to enter are still validated for
    // completeness; nothing on this step is required.
    if (isRenewal) {
      const offersDentalOnRenewal = state.offeringDental !== "No";
      const offersVisionOnRenewal = state.offeringVision !== "No";
      return {
        employees: [employeesComplete(availablePlans, offersDentalOnRenewal, offersVisionOnRenewal)],
      };
    }

    // Required Documents section visibility — hidden when 20+ enrolled AND replacing medical
    const totalEnrolled = state.totalEnrolledEmployees;
    const showRequiredDocs = isRequiredDocsVisible(totalEnrolled, state.replacingMedical);

    if (showRequiredDocs) {
      schema.docVerificationType = [required("Employment verification document is required")];
      if (state.docVerificationType === "State Wage and Tax") {
        schema["documents.wageTax"] = [fileRequired("State Quarterly Wage & Tax document is required")];
      }
      if (state.docVerificationType === "Payroll Records") {
        schema["documents.payroll"] = [fileRequired("Payroll records document is required")];
      }
      if (state.docVerificationType === "Business in operation less than 90 days") {
        schema["documents.newBusinessDocs"] = [fileRequired("New business documentation is required")];
      }
    }

    // Prior Carrier Bill — own section, required when replacing existing medical coverage
    if (state.replacingMedical === "Yes") {
      schema["documents.priorBill"] = [fileRequired("Prior carrier bill is required")];
    }

    // Owner Eligibility — newlyFormedCompany required when toggle is visible
    const showOwnerEligibility = isOwnerEligibilityVisible(state.ownershipAssignments);
    if (showOwnerEligibility && !showRequiredDocs) {
      schema.newlyFormedCompany = [required("Please select Yes or No")];
    }

    // ownerEligibilityDocs is intentionally NOT required per AC

    // Employee listing — non-Claims requires at least one employee (census or
    // manual); Claims requires the census file itself (no employee list exists).
    // offersDental/offersVision derived from the user's Coverage-page answer.
    const offersDental = state.offeringDental !== "No";
    const offersVision = state.offeringVision !== "No";
    if (uwMethod === "Claims") {
      schema["documents.censusFile"] = [fileRequired("An eligibility census file is required")];
    } else {
      schema.employees = [
        minArrayLength(1, "Please upload a census file or add employees manually"),
        employeesComplete(availablePlans, offersDental, offersVision),
      ];
    }

    return schema;
  }) as StepSchema,
  8: {},
};
